// Ops live board: fixed five columns, filters, counts.

/** @typedef {import('@popcart/core/v1').OpsBoardColumn} OpsBoardColumn */
/** @typedef {OpsBoardColumn['stage']} BoardStage */
/** @typedef {OpsBoardColumn['orders'][number]} BoardOrder */

/** @type {{ stage: BoardStage; label: string }[]} */
export const BOARD_STAGES = [
  { stage: 'new', label: 'New' },
  { stage: 'preparing', label: 'Preparing' },
  { stage: 'ready', label: 'Ready' },
  { stage: 'on_the_way', label: 'On the way' },
  { stage: 'delivered', label: 'Delivered' },
];

/**
 * Always returns the five columns in board order, whatever the server sent (missing columns
 * are empty, unknown ones are dropped). `count` falls back to the number of orders.
 * @param {OpsBoardColumn[] | null | undefined} columns
 * @returns {(OpsBoardColumn & { label: string })[]}
 */
export function normaliseBoard(columns) {
  const list = Array.isArray(columns) ? columns : [];
  return BOARD_STAGES.map(({ stage, label }) => {
    const col = list.find((c) => c && c.stage === stage);
    const orders = col && Array.isArray(col.orders) ? col.orders : [];
    const n = col && Number.isFinite(col.count) ? col.count : orders.length;
    return { stage, label, count: n, orders };
  });
}

/**
 * @typedef {{ shop?: string | null; lateOnly?: boolean; q?: string }} BoardFilter
 */

/**
 * Filter the tickets on every column. Express tickets float to the top, then late, then the
 * longest-waiting. Counts reflect the server's totals when unfiltered, else the visible tickets.
 * @param {(OpsBoardColumn & { label: string })[]} board
 * @param {BoardFilter} filter
 */
export function filterBoard(board, filter) {
  const q = (filter.q || '').trim().toLowerCase();
  const active = !!(filter.shop || filter.lateOnly || q);
  return board.map((col) => {
    const orders = col.orders
      .filter((o) => !filter.shop || o.shopNames.includes(filter.shop))
      .filter((o) => !filter.lateOnly || o.late)
      .filter((o) => !q || o.code.toLowerCase().includes(q) || o.addressShort.toLowerCase().includes(q) || o.shopNames.some((s) => s.toLowerCase().includes(q)))
      .slice()
      .sort(compareTickets);
    return { ...col, orders, count: active ? orders.length : col.count };
  });
}

/** @param {BoardOrder} a @param {BoardOrder} b */
function compareTickets(a, b) {
  if (a.speed !== b.speed) return a.speed === 'express' ? -1 : 1;
  if (a.late !== b.late) return a.late ? -1 : 1;
  return b.minutes - a.minutes;
}

/** Distinct shop names across the board, for the filter pills. @param {OpsBoardColumn[]} board */
export function boardShopNames(board) {
  /** @type {string[]} */
  const names = [];
  for (const c of board) for (const o of c.orders) for (const s of o.shopNames) if (!names.includes(s)) names.push(s);
  return names.sort();
}

/** Late tickets not yet delivered. @param {OpsBoardColumn[]} board */
export function lateCount(board) {
  let n = 0;
  for (const c of board) if (c.stage !== 'delivered') for (const o of c.orders) if (o.late) n++;
  return n;
}

/** Tickets on the board that aren't delivered yet (the "Live orders" nav count). @param {OpsBoardColumn[]} board */
export function liveCount(board) {
  return board.filter((c) => c.stage !== 'delivered').reduce((sum, c) => sum + c.count, 0);
}

/**
 * Plot rider positions on a schematic: scale lat/lng into 8–92 % of the box so dots never clip.
 * @param {{ lat: number; lng: number }[]} points
 * @returns {{ x: number; y: number }[]} percentages, y grows downward (north is up)
 */
export function schematicPositions(points) {
  if (!points.length) return [];
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const spanLat = maxLat - minLat || 1;
  const spanLng = maxLng - minLng || 1;
  return points.map((p) => ({
    x: maxLng === minLng ? 50 : 8 + ((p.lng - minLng) / spanLng) * 84,
    y: maxLat === minLat ? 50 : 8 + ((maxLat - p.lat) / spanLat) * 84,
  }));
}
